import { useEffect, useRef, useState } from "react";
import { menuItems } from "@/mocks/content";

interface CardProps {
  item: typeof menuItems[0];
  index: number;
  visible: boolean;
}

function ServiceCard({ item, index, visible }: CardProps) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      className="relative bg-[#FAFAF8] overflow-hidden cursor-pointer"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible
          ? `translateY(0) translateX(${index % 2 === 0 ? "0" : "0"})`
          : `translateY(50px)`,
        transition: `opacity 0.7s ease ${index * 0.12}s, transform 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) ${index * 0.12}s`,
        perspective: "1000px",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div
        style={{
          transform: hovered ? "rotateY(3deg) rotateX(-2deg) scale(1.02)" : "rotateY(0) rotateX(0) scale(1)",
          transition: "transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
          transformStyle: "preserve-3d",
        }}
      >
        {/* Image */}
        <div className="w-full aspect-video overflow-hidden">
          <img
            src={item.image}
            alt={item.name}
            className="w-full h-full object-cover object-top transition-transform duration-700"
            style={{ transform: hovered ? "scale(1.06)" : "scale(1)" }}
          />
        </div>

        {/* Content */}
        <div className="p-6 md:p-8 border border-[#E8E6E1] border-t-0">
          <div className="flex items-start justify-between mb-3">
            <span
              className="text-[#8B7355]/60 text-xs tracking-[0.3em] font-light"
              style={{ fontFamily: "'Noto Serif JP', serif" }}
            >
              TOP {item.id}
            </span>
            <span
              className="text-[#4A4A4A] text-xs tracking-[0.1em]"
              style={{ fontFamily: "'Noto Serif JP', serif" }}
            >
              {item.duration}
            </span>
          </div>
          <h3
            className="text-[#2C2C2C] font-light mb-3 leading-snug"
            style={{ fontFamily: "'Noto Serif JP', serif", fontSize: "clamp(1rem, 2vw, 1.25rem)" }}
          >
            {item.name}
          </h3>
          <p
            className="text-[#4A4A4A] text-sm leading-relaxed mb-4 font-light"
            style={{ fontFamily: "'Noto Serif JP', serif" }}
          >
            {item.description}
          </p>
          <div className="flex items-center justify-between pt-4 border-t border-[#E8E6E1]">
            <span
              className="text-[#8B7355] font-light"
              style={{ fontFamily: "'Noto Serif JP', serif", fontSize: "1.1rem" }}
            >
              {item.price}
            </span>
            <span className="text-[#4A4A4A] text-xs tracking-[0.1em]" style={{ fontFamily: "'Noto Serif JP', serif" }}>
              税込
            </span>
          </div>
        </div>

        {/* Shadow Lift */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            boxShadow: hovered
              ? "0 20px 60px rgba(44, 44, 44, 0.18)"
              : "0 4px 20px rgba(44, 44, 44, 0.06)",
            transition: "box-shadow 0.4s ease",
          }}
        />
      </div>
    </div>
  );
}

export default function ServicesSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="services" ref={sectionRef} className="py-24 md:py-40 bg-[#F4F2EE]">
      <div className="max-w-7xl mx-auto px-6 md:px-16">
        {/* Header */}
        <div className="mb-16 md:mb-24">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <p
                className="text-[#8B7355] text-xs tracking-[0.4em] mb-3 font-light"
                style={{
                  fontFamily: "'Noto Serif JP', serif",
                  opacity: visible ? 1 : 0,
                  transition: "opacity 0.6s ease 0.2s",
                }}
              >
                POPULAR MENU
              </p>
              <h2
                className="text-[#2C2C2C] font-light leading-tight"
                style={{
                  fontFamily: "'Noto Serif JP', serif",
                  fontSize: "clamp(1.8rem, 3.5vw, 3rem)",
                  opacity: visible ? 1 : 0,
                  transform: visible ? "translateY(0)" : "translateY(20px)",
                  transition: "opacity 0.7s ease 0.3s, transform 0.7s ease 0.3s",
                }}
              >
                人気メニュー TOP5
              </h2>
            </div>
            <p
              className="text-[#4A4A4A] text-sm font-light max-w-xs leading-relaxed"
              style={{
                fontFamily: "'Noto Serif JP', serif",
                opacity: visible ? 1 : 0,
                transition: "opacity 0.6s ease 0.5s",
              }}
            >
              お客様のご要望に合わせた、多彩なコースをご用意しております。
            </p>
          </div>
        </div>

        {/* Bento Box + Staircase Grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
          {/* Card 1 — Large */}
          <div className="md:col-span-7">
            <ServiceCard item={menuItems[0]} index={0} visible={visible} />
          </div>
          {/* Card 2 — Small, offset */}
          <div className="md:col-span-5 md:mt-12">
            <ServiceCard item={menuItems[1]} index={1} visible={visible} />
          </div>
          {/* Card 3 — Small */}
          <div className="md:col-span-5">
            <ServiceCard item={menuItems[2]} index={2} visible={visible} />
          </div>
          {/* Card 4 — Medium, offset */}
          <div className="md:col-span-7 md:mt-8">
            <ServiceCard item={menuItems[3]} index={3} visible={visible} />
          </div>
          {/* Card 5 — Full width accent */}
          <div className="md:col-span-12 md:mt-4">
            <div
              className="relative overflow-hidden"
              style={{
                opacity: visible ? 1 : 0,
                transform: visible ? "translateY(0)" : "translateY(50px)",
                transition: "opacity 0.7s ease 0.6s, transform 0.7s ease 0.6s",
              }}
            >
              <div className="grid grid-cols-1 md:grid-cols-2">
                <div className="aspect-video md:aspect-auto md:h-72 overflow-hidden">
                  <img
                    src={menuItems[4].image}
                    alt={menuItems[4].name}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="bg-[#2C2C2C] p-8 md:p-12 flex flex-col justify-center">
                  <span
                    className="text-[#8B7355] text-xs tracking-[0.3em] mb-4 font-light"
                    style={{ fontFamily: "'Noto Serif JP', serif" }}
                  >
                    PREMIUM — TOP 5
                  </span>
                  <h3
                    className="text-[#FAFAF8] font-light mb-4 leading-snug"
                    style={{ fontFamily: "'Noto Serif JP', serif", fontSize: "clamp(1.2rem, 2.5vw, 1.8rem)" }}
                  >
                    {menuItems[4].name}
                  </h3>
                  <p
                    className="text-[#FAFAF8]/60 text-sm leading-relaxed mb-6 font-light"
                    style={{ fontFamily: "'Noto Serif JP', serif" }}
                  >
                    {menuItems[4].description}
                  </p>
                  <div className="flex items-center gap-6">
                    <span
                      className="text-[#8B7355] font-light text-xl"
                      style={{ fontFamily: "'Noto Serif JP', serif" }}
                    >
                      {menuItems[4].price}
                    </span>
                    <span className="text-[#FAFAF8]/40 text-xs" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                      {menuItems[4].duration} / 税込
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
